import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { IndeterminateCheckbox } from '../Förvaltning/MaintenanceDraft/components/ui/IndeterminateCheckbox';
import { KEY_OBJECT_GROUPS, KeyObject, KeyObjectsByBuilding } from './keyObjects';
import { buildingState, commonState, KeySelection, objectsState, setCommon, setObjects, toggleBuilding } from './keySelection';
import { normalizeQuery } from './keyRegisterView';

interface KeyPlaceTreeProps {
    buildings: Array<{ key: string; name: string }>;
    objectsByBuilding: KeyObjectsByBuilding;
    loadingBuildings: { [buildingKey: string]: true };
    /** Objects some key type is linked to; the others are muted. */
    linkedObjectKeys: ReadonlySet<string>;
    /** Keys per building, shown on its row. */
    keyCounts: { [buildingKey: string]: number };
    selection: KeySelection;
    onChange: (selection: KeySelection) => void;
    /** Narrows to buildings and objects whose name contains it, opening the matches. */
    query: string;
}

/** Buildings and groups open from the start: those with a partial pick. */
const initialExpanded = (selection: KeySelection, objectsByBuilding: KeyObjectsByBuilding): Set<string> => {
    const open = new Set<string>();
    for (const [buildingKey, pick] of Object.entries(selection)) {
        if (pick === 'all') continue;
        open.add(buildingKey);
        for (const object of objectsByBuilding[buildingKey] ?? []) {
            if (pick.objectKeys.includes(object.key)) open.add(`${buildingKey}/${object.group}`);
        }
    }
    return open;
};

const contains = (name: string, normalized: string) => name.toLocaleLowerCase('sv').includes(normalized);

/**
 * The filter modal's tree: building › Gemensamma nycklar, Lägenheter, Lokaler, Förråd › objects.
 * A building's box picks the whole building; Gemensamma picks only the keys that open the building itself.
 */
const KeyPlaceTree: React.FC<KeyPlaceTreeProps> = ({
    buildings, objectsByBuilding, loadingBuildings, linkedObjectKeys, keyCounts, selection, onChange, query,
}) => {
    const [expanded, setExpanded] = useState(() => initialExpanded(selection, objectsByBuilding));
    const normalized = normalizeQuery(query);

    const toggleOpen = (id: string) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const chevron = (id: string, open: boolean, label: string) => (
        <button
            type="button"
            className="key-tree-chevron"
            aria-expanded={open}
            aria-label={`${open ? 'Dölj' : 'Visa'} ${label}`}
            onClick={() => toggleOpen(id)}
        >
            {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        </button>
    );

    const rows = buildings.flatMap(building => {
        const objects = objectsByBuilding[building.key];
        const nameMatch = !normalized || contains(building.name, normalized);
        const visible = (objects ?? []).filter(o => nameMatch || contains(o.name, normalized));
        if (!nameMatch && visible.length === 0) return [];
        const searchOpen = !nameMatch;
        const open = searchOpen || expanded.has(building.key);
        const pick = selection[building.key];
        const state = buildingState(pick);
        const all = objects ?? [];
        const keyCount = keyCounts[building.key] ?? 0;

        return [(
            <li key={building.key} className="key-tree-building">
                <div className="key-tree-row">
                    {chevron(building.key, open, `innehåll i ${building.name}`)}
                    <label className="key-tree-label key-tree-label--building">
                        <IndeterminateCheckbox
                            className="form-check-input"
                            checked={state.checked}
                            indeterminate={state.indeterminate}
                            onChange={() => onChange(toggleBuilding(selection, building.key))}
                        />
                        <span>{building.name}</span>
                    </label>
                    <span className="key-tree-hint">{keyCount === 1 ? '1 nyckel' : `${keyCount} nycklar`}</span>
                </div>
                {open && (
                    <ul className="key-tree-children">
                        {nameMatch && (
                            <li className="key-tree-row key-tree-row--leaf">
                                <label className="key-tree-label">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={commonState(pick)}
                                        onChange={e => onChange(setCommon(selection, building.key, e.target.checked, all))}
                                    />
                                    <span>Gemensamma nycklar</span>
                                    <span className="key-tree-hint">port, tvättstuga …</span>
                                </label>
                            </li>
                        )}
                        {!objects && (
                            <li className="key-tree-status">
                                {loadingBuildings[building.key] ? 'Läser in lägenheter, lokaler och förråd…' : 'Kunde inte läsa in lägenheter, lokaler och förråd.'}
                            </li>
                        )}
                        {KEY_OBJECT_GROUPS.map(group => {
                            const inGroup = visible.filter(o => o.group === group);
                            if (inGroup.length === 0) return null;
                            const id = `${building.key}/${group}`;
                            const groupOpen = searchOpen || expanded.has(id);
                            const keys = inGroup.map(o => o.key);
                            const groupState = objectsState(pick, keys);
                            return (
                                <li key={group}>
                                    <div className="key-tree-row">
                                        {chevron(id, groupOpen, `${group.toLocaleLowerCase('sv')} i ${building.name}`)}
                                        <label className="key-tree-label">
                                            <IndeterminateCheckbox
                                                className="form-check-input"
                                                checked={groupState.checked}
                                                indeterminate={groupState.indeterminate}
                                                onChange={() => onChange(setObjects(selection, building.key, keys, !groupState.checked, all))}
                                            />
                                            <span>{group}</span>
                                            <span className="key-tree-hint">{inGroup.length}</span>
                                        </label>
                                    </div>
                                    {groupOpen && (
                                        <ul className="key-tree-children">
                                            {inGroup.map((object: KeyObject) => (
                                                <li key={object.key} className="key-tree-row key-tree-row--leaf">
                                                    <label className={`key-tree-label${linkedObjectKeys.has(object.key) ? '' : ' key-tree-label--unlinked'}`}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={objectsState(pick, [object.key]).checked}
                                                            onChange={e => onChange(setObjects(selection, building.key, [object.key], e.target.checked, all))}
                                                        />
                                                        <span>{object.name}</span>
                                                        {!linkedObjectKeys.has(object.key) && <span className="key-tree-hint">inga nycklar</span>}
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </li>
        )];
    });

    if (rows.length === 0) {
        return <p className="key-tree-empty">{normalized ? 'Inga byggnader eller objekt matchar sökningen.' : 'Inga byggnader.'}</p>;
    }
    return <ul className="key-tree" aria-label="Byggnader, lägenheter, lokaler och förråd">{rows}</ul>;
};

export default KeyPlaceTree;
